import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCalls, getCallsExport } from '../api';
import { formatMx } from '../utils/datetime';
import { formatNumber } from '../utils/format';
import AnalyticsFilterBar from '../components/AnalyticsFilterBar';
import { colorV2 } from '../components/BoletaV2';
import { EstadoAprovechamiento, aprovechamiento } from '../components/AprovechableBoleta';
import { ThOrdenable } from '../utils/ordenarTabla';
import DownloadButton from '../components/DownloadButton';
import { usePeticionVigente } from '../utils/peticionVigente';

/**
 * El sentimiento de la llamada.
 *
 * El valor lo CALCULA el servidor (`analysis.sentimiento`) a partir de la
 * satisfacción estimada por el LLM. La pantalla no aplica umbrales: si lo
 * hiciera habría una tercera copia de la regla —la ficha del agente y el filtro
 * son las otras dos— y las tres se separarían el día que alguien mueva un
 * corte. Ver `app/sentimiento.py`.
 *
 * Antes leía `call.overall_sentiment`, que la alimenta Deepgram: para español
 * casi no devuelve sentimiento, así que la columna salía vacía justo donde
 * importa. Lo reportó Armando el 10-sep-2026 con una llamada de nota 93 y la
 * celda en blanco.
 *
 * Sin estimación se pinta "—" y no "neutral": una llamada sin analizar no es
 * una llamada neutral, y confundirlas mete a todo el corpus sin analizar en el
 * montón del medio.
 */
function SentimentBadge({ sentiment }) {
  if (!sentiment) return <span style={{ color: '#4b5563' }}>—</span>;
  return <span className={`badge badge-${sentiment}`}>{sentiment}</span>;
}

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
// 'YYYY-MM-DD' sin pasar por UTC (en México sería el día anterior).
const diaMes = (iso) => {
  if (!iso) return null;
  const [, m, d] = iso.split('-').map(Number);
  return `${d} ${MESES[m - 1]}`;
};
const rangoFechas = (c) => {
  const a = diaMes(c.entrada);
  const b = diaMes(c.salida);
  if (a && b) return `${a} – ${b}`;
  return a || b;
};
const montoTexto = (c) => (c.monto != null
  ? `${Number(c.monto).toLocaleString('es-MX', { maximumFractionDigits: 2 })}${c.moneda ? ` ${c.moneda}` : ''}`
  : null);

/**
 * Datos para recontactar (pedido de los dueños del negocio, 24-sep-2026): quién
 * es el cliente, cómo contactarlo, qué pidió y qué pasó — para que el equipo de
 * ventas le regrese la llamada a quien no compró. Todo lo extrae YA el análisis
 * de la llamada (`app/recontacto.py`); aquí sólo se junta. Sólo lo reciben los
 * supervisores.
 */
function CeldaContacto({ contacto: c }) {
  if (!c) return <span style={{ color: '#4b5563' }}>-</span>;
  const pedido = [c.hotel, rangoFechas(c), c.huespedes ? `${c.huespedes} huésped${c.huespedes === 1 ? '' : 'es'}` : null,
    c.habitacion, montoTexto(c)].filter(Boolean);
  const vacio = !c.nombre && !c.telefono && !c.correo && pedido.length === 0;
  if (vacio) return <span style={{ color: '#4b5563' }} title="El análisis no encontró datos de contacto ni de la reserva en esta llamada.">sin datos</span>;
  return (
    <div style={{ minWidth: 260, maxWidth: 340, whiteSpace: 'normal', lineHeight: 1.4, fontSize: 11.5 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <b style={{ color: '#e6e6ec', fontSize: 12 }}>{c.nombre || 'Sin nombre'}</b>
        {c.recontactable && (
          <span title="No compró y dejó teléfono o correo"
                style={{ fontSize: 9.5, fontWeight: 700, color: '#fbbf24', border: '1px solid rgba(251,191,36,.45)',
                         borderRadius: 999, padding: '0 6px' }}>para recontactar</span>
        )}
      </div>
      {(c.telefono || c.correo) && (
        <div style={{ color: '#c8c8d0' }}>{[c.telefono, c.correo].filter(Boolean).join(' · ')}</div>
      )}
      {pedido.length > 0 && <div style={{ color: '#9a9aa8' }}>{pedido.join(' · ')}</div>}
      {c.que_paso && (
        <div title={c.que_paso}
             style={{ color: '#8b8b98', fontStyle: 'italic', display: '-webkit-box', WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {c.que_paso}
        </div>
      )}
    </div>
  );
}

const scoreColor = (s) => {
  if (s >= 75) return '#34d399';
  if (s >= 50) return '#fbbf24';
  if (s >= 25) return '#fb923c';
  return '#f87171';
};

export default function CallsExplorer() {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Valor "debounced": es el que realmente se manda al API. La búsqueda en
  // transcripción es costosa (ILIKE), así que NO disparamos una consulta por
  // cada tecla — solo 400ms después de que el usuario deja de escribir, y con
  // 3+ caracteres. Sin esto, teclear generaba una ráfaga de full-scans que
  // saturaba la BD y colgaba todo el dashboard.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // "No venta con contacto": la lista para regresarle la llamada a quien no compró.
  const [recontactar, setRecontactar] = useState(false);
  // El orden viaja al SERVIDOR, no se hace aquí: la tabla está paginada, y
  // ordenar las 50 filas visibles daría un "las de mayor score" que en realidad
  // es "las de mayor score de esta página". Ver `_ORDENABLES` en routers/calls.py.
  const [sortBy, setSortBy] = useState('start_time');
  const [sortDir, setSortDir] = useState('desc');
  const orden = {
    clave: sortBy,
    dir: sortDir,
    alternar: (col) => {
      if (col === sortBy) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(col);
        setSortDir('desc');
      }
      setPage(1);  // el orden cambia qué cae en la página 1
    },
  };

  // Filtros emitidos por AnalyticsFilterBar (objeto listo para el API). null
  // hasta que la barra emite (incl. la semana en curso por defecto), para que
  // la primera consulta ya venga acotada y no traiga TODO el histórico.
  const [extra, setExtra] = useState(null);
  const nuevaPeticion = usePeticionVigente();

  const fetchCalls = useCallback(() => {
    if (!extra) return; // espera a que la barra emita los filtros
    const vigente = nuevaPeticion();
    setLoading(true);
    const params = { page, page_size: 30, sort_by: sortBy, sort_dir: sortDir, ...extra };
    if (debouncedSearch) params.search = debouncedSearch;
    if (recontactar) params.recontactar = true;

    getCalls(params).then(r => {
      if (!vigente()) return;
      setCalls(r.data.items);
      setTotal(r.data.total);
      setTotalPages(r.data.total_pages);
      setLoading(false);
    }).catch(() => { if (vigente()) setLoading(false); });
  }, [page, debouncedSearch, extra, sortBy, sortDir, recontactar, nuevaPeticion]);

  useEffect(() => { fetchCalls(); }, [fetchCalls]);

  // Descarga TODO lo que cumple los filtros actuales, no sólo la página
  // visible: antes bajar el filtrado completo era repetir "Descargar CSV"
  // página por página. El servidor arma el archivo completo en una sola
  // consulta (paginada por llave puertas adentro, `GET /calls/export`) y aquí
  // sólo se dispara la descarga del blob que regresa.
  const handleDownloadAll = useCallback(async () => {
    if (!extra) return;
    const params = { ...extra };
    if (debouncedSearch) params.search = debouncedSearch;
    if (recontactar) params.recontactar = true;

    const r = await getCallsExport(params);
    const blob = new Blob([r.data], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `llamadas_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [extra, debouncedSearch, recontactar]);

  // Debounce: aplica la búsqueda 400ms después de la última tecla (y solo con
  // 3+ caracteres). Resetea a la página 1 cuando cambia el término.
  useEffect(() => {
    const term = search.trim();
    const t = setTimeout(() => {
      setDebouncedSearch(term.length >= 3 ? term : '');
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const formatDuration = (s) => {
    if (!s) return '-';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  const formatDate = (d) => formatMx(d, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div>
      <h1 className="page-title">Llamadas</h1>
      <p className="page-subtitle">{formatNumber(total)} llamadas {recontactar ? 'sin venta y con contacto' : 'procesadas'}</p>

      <div className="filters-bar" style={{ marginBottom: 12 }}>
        <input className="filter-input" placeholder="Buscar en transcripcion (min. 3 letras)..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: 240 }} />
        <button type="button" aria-pressed={recontactar}
                onClick={() => { setRecontactar((v) => !v); setPage(1); }}
                title="Llamadas que no terminaron en venta, con conversación de verdad y con teléfono o correo del cliente"
                style={{ padding: '7px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                         fontFamily: 'inherit', color: recontactar ? '#0f0f13' : '#fbbf24',
                         background: recontactar ? '#fbbf24' : 'transparent', border: '1px solid rgba(251,191,36,.55)' }}>
          {recontactar ? '✓ ' : ''}No venta con contacto
        </button>
      </div>
      <AnalyticsFilterBar onChange={(p) => { setExtra(p); setPage(1); }} showTypification preseleccionTipificacion showConducta />

      {loading ? (
        <div className="loading">Cargando...</div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, marginBottom: 8 }}>
            <DownloadButton
              onDownload={handleDownloadAll}
              label={`Descargar CSV (${formatNumber(total)})`}
            />
          </div>

          {/* `overflowX: auto`: con la columna de contacto la tabla puede pasar
              del ancho de la pantalla, y cortarla escondería justo esa columna. */}
          <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <ThOrdenable columna="start_time" orden={orden}>Fecha</ThOrdenable>
                  <ThOrdenable columna="duration_seconds" orden={orden}>Duracion</ThOrdenable>
                  <ThOrdenable columna="agent_name" orden={orden}>Agente</ThOrdenable>
                  <ThOrdenable columna="call_category" orden={orden}>Categoria</ThOrdenable>
                  {/* Aprovechamiento no se ordena: la columna muestra un
                      distintivo sólo en las excluidas, y ordenar por eso es lo
                      que ya hace el filtro de la barra, mejor. */}
                  <th>Aprovech.</th>
                  {/* Las dos tipificaciones juntas, en una sola columna: lo que
                      se quiere ver de un jalón es si concuerdan, y dos columnas
                      separadas obligan a comparar saltando la vista. */}
                  <th>Tipificacion</th>
                  <ThOrdenable columna="quality_score" orden={orden}>Score</ThOrdenable>
                  {/* La v2 al lado de la v1, no en su lugar. Son escalas
                      distintas —0-100 contra 0-10— y verlas juntas sobre la
                      misma llamada es el punto mientras Gerencia decide cuál
                      manda. Vacía en todo lo anterior al 1-ago-2026, que es
                      cuando empieza a existir la v2. */}
                  <ThOrdenable columna="calificacion_v2" orden={orden} title="Score V2: calificación de 10 puntos donde cada criterio pesa lo que cuesta no hacerlo. No es comparable con el Score de la izquierda, que va de 0 a 100.">Score V2</ThOrdenable>
                  <ThOrdenable columna="sentimiento" orden={orden}>Sentimiento</ThOrdenable>
                  <th>Resumen</th>
                  <th title="Contacto del cliente, lo que pidió y qué pasó. Sale del análisis de la llamada.">Datos para recontactar</th>
                </tr>
              </thead>
              <tbody>
                {calls.map(c => (
                  <tr key={c.id} onClick={() => navigate(`/calls/${c.id}`)}>
                    <td>{formatDate(c.start_time)}</td>
                    <td>{formatDuration(c.duration_seconds)}</td>
                    <td>{c.agent_name || <span style={{ color: '#4b5563' }}>-</span>}</td>
                    <td>
                      {c.analysis?.call_category ? (
                        <span className="badge" style={{ background: 'rgba(99,102,241,0.12)', color: '#a5b4fc', fontSize: 10 }}>
                          {c.analysis.call_category}
                        </span>
                      ) : '-'}
                    </td>
                    <td>
                      {/* Sólo se marca lo que NO es aprovechable: una columna con
                          un distintivo verde en cada fila sería ruido. El resto
                          se lee por ausencia. */}
                      {c.analysis && aprovechamiento(c.analysis) === false
                        ? <EstadoAprovechamiento analysis={c.analysis} />
                        : <span style={{ color: '#4b5563' }}>-</span>}
                    </td>
                    <td><CeldaTipificacion call={c} /></td>
                    <td>
                      {c.analysis?.quality_score != null ? (
                        <span style={{ fontWeight: 700, color: scoreColor(c.analysis.quality_score) }}>
                          {c.analysis.quality_score}
                        </span>
                      ) : '-'}
                    </td>
                    <td>
                      {c.calificacion_v2 != null ? (
                        <span
                          style={{ fontWeight: 700, color: colorV2(c.calificacion_v2) }}
                          title="Score V2: calificación de 10 puntos donde cada criterio pesa lo que cuesta no hacerlo. No es comparable con el Score de la izquierda, que va de 0 a 100."
                        >
                          {c.calificacion_v2.toFixed(1)}
                        </span>
                      ) : (
                        <span style={{ color: '#4b5563' }} title="Sin boleta v2: la llamada es anterior al 1-ago-2026, es saliente, o aplicó menos de la mitad de la rúbrica.">-</span>
                      )}
                    </td>
                    <td><SentimentBadge sentiment={c.analysis?.sentimiento} /></td>
                    <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.analysis?.summary_es || c.summary || '-'}
                    </td>
                    <td><CeldaContacto contacto={c.contacto} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button onClick={() => setPage(p => p - 1)} disabled={page <= 1}>Anterior</button>
            <span>Pagina {page} de {totalPages}</span>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>Siguiente</button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Las dos conclusiones de una llamada, apiladas y compactas.
 *
 * DOS renglones y no tres: ya no existen "v1" y "v2" como cosas distintas. El
 * análisis emite el catálogo VIGENTE EL DÍA DE LA LLAMADA —el viejo hasta el
 * 18-ago-2026, el nuevo desde el 19— así que hay una sola conclusión de la IA.
 * Ver `constants.CATALOGO_V2_DESDE`.
 *
 * Mostrar una fila "v2" que nunca se llena era decirle al usuario que falta un
 * dato que no existe.
 */
function CeldaTipificacion({ call }) {
  const a = call.analysis || {};

  const fila = (etiqueta, valor, color) => (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', lineHeight: 1.4 }}>
      <span style={{ color: '#4b5563', fontSize: 9.5, minWidth: 42 }}>{etiqueta}</span>
      <span style={{ color: valor ? color : '#4b5563', fontSize: 10.5 }}>
        {valor || 'vacia'}
      </span>
    </div>
  );

  return (
    <div style={{ minWidth: 200, maxWidth: 260 }}>
      {fila('Agente', call.genesys_conclusion, '#cbd5e1')}
      {fila('IA', a.conclusion_code, '#a5b4fc')}
    </div>
  );
}
